import React, {
  cloneElement,
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { kebabCase } from '../../../utils/StringUtils';

// Utils
import { scrollListboxTo, scrollEntireViewportTo } from '../utils/scrollToItem';
import { getCharIndex, isValidChar } from '../utils/typeahead';

// values and functions that are hard to mock so are instead exported
// and mutated in tests
export const _ = {
  useListboxRef() {
    return useRef();
  }
};

const keyCode = {
  DOWN: 'ArrowDown',
  DOWN_IE: 'Down',
  END: 'End',
  ESC_IE: 'ESC',
  ESC: 'Escape',
  HOME: 'Home',
  PAGEDOWN: 'PageDown',
  PAGEUP: 'PageUp',
  ENTER: 'Enter',
  SPACE: ' ',
  TAB: 'Tab',
  UP: 'ArrowUp',
  UP_IE: 'Up',
  RIGHT: 'ArrowRight',
  RIGHT_IE: 'Right',
  LEFT: 'ArrowLeft',
  LEFT_IE: 'Left'
};

function recursiveMap(children, fn) {
  if (!Array.isArray(children)) {
    return children;
  }
  return React.Children.map(children, (child, idx) => {
    if (!React.isValidElement(child)) {
      return child;
    }

    if (child.props.children) {
      child = React.cloneElement(child, {
        children: recursiveMap(child.props.children, fn)
      });
    }

    return fn(child, idx);
  });
}

const CSS_PREFIX = 'vds-listbox-container';

const CybsListboxContainer = forwardRef(
  (
    {
      children,
      className,
      enableListboxKeydown,
      focusedIndex: focusedIndexProp,
      focusedValue: focusedValueProp,
      items,
      level,
      name,
      onKeyDown,
      optionsTemplate,
      multiselect,
      onSelect,
      onScrollBottom,
      scrollOnLoad,
      selectedValues,
      selectedListItems,
      tabIndex,
      type,
      heightOfItem,
      numberValuesRendered,
      onLoadMoreItems,
      ...remainingProps
    },
    ref
  ) => {
    // Refs
    const listboxRef = _.useListboxRef();

    // Hooks
    const [selected, setSelected] = useState(
      typeof selectedListItems === 'string'
        ? [selectedListItems]
        : selectedListItems
    );
    const [focusableItemElements, setfocusableItemElements] = useState([]);
    const [focusableItemObject, setFocusableItemObjects] = useState([]);
    const [focusedIndex, setFocusedIndex] = useState(focusedIndexProp);
    const [focusedValue, setFocusedValue] = useState(focusedValueProp);
    // List of all focusable list items (not disabled).

    // On first load, make sure the listbox is visible
    useLayoutEffect(() => {
      if (scrollOnLoad) {
        scrollEntireViewportTo(listboxRef.current);
      }
    }, []);

    useEffect(() => {
      if (listboxRef.current) {
        setfocusableItemElements(
          listboxRef.current.querySelectorAll(
            'ul > li:not(.vds-state--disabled)'
          )
        );
        if (items && enableListboxKeydown) {
          const newFocusableItemObject = [];
          for (let i = 0; i < items.length; i++) {
            if (items[i].categories && items[i].categories.length) {
              newFocusableItemObject.push(
                ...items[i].categories.filter(el => !el.disabled)
              );
            } else if (!items[i].disabled) {
              newFocusableItemObject.push(items[i]);
            }
          }
          setFocusableItemObjects(newFocusableItemObject);
        }
      }
    }, [enableListboxKeydown, items, numberValuesRendered]);

    useEffect(() => {
      // Nested type listboxes should only scroll at the active level (used for combobox)
      if (type !== 'nested' || selectedListItems.length - 1 === level) {
        scrollListboxTo({
          element: focusableItemElements[focusedIndex],
          listboxNode: listboxRef.current
        });
      }
    }, [focusedIndex, focusableItemElements]);

    useEffect(() => {
      setSelected(selectedListItems);
    }, [selectedListItems]);

    useEffect(() => {
      setFocusedValue(focusedValueProp);
    }, [focusedValueProp]);

    useEffect(() => {
      setFocusedIndex(focusedIndexProp);
    }, [focusedIndexProp]);

    const onScrollDown = () => {
      let ticking = false;
      const windowScrolly = listboxRef.current.scrollTop;
      const listScrollTop = listboxRef.current.scrollTop;
      const listScrollHeight = listboxRef.current.scrollHeight;
      const listScrollClientHeight = listboxRef.current.clientHeight;
      const bottom = Math.abs(
        listScrollHeight - listScrollTop - listScrollClientHeight
      );
      if (items.length && items[0].value !== 'no_results' && bottom < 1.5) {
        if (typeof onScrollBottom === 'function') {
          onScrollBottom();
        }
      }
      function renderMoreItems() {
        // Do something with the scroll position
        // const newScrollPosition = windowScrolly/heightOfItem;
        // Some of these values that are not being used currently could be useful for v2
        // const difference = Math.abs(windowScrolly - newScrollPosition);
        if (
          windowScrolly + heightOfItem >
            numberValuesRendered * heightOfItem * 0.8 &&
          items.length > numberValuesRendered
        ) {
          onLoadMoreItems();
        }
      }

      if (!ticking) {
        window.requestAnimationFrame(() => {
          renderMoreItems();
          ticking = false;
        });

        ticking = true;
      }

      // TO DO: Make this logic trigger only on keyboard up
      // // Fix for Single/Multi Combobox when the focus is on the topmost menu item
      // if ((type === 'single' || type === 'multi') && focusedIndex === 0) {
      //   listboxRef.current.scrollTop = 0;
      // }
    };

    // Click event on a list item.
    const handleClick = (event, item) => {
      if (event) {
        event.stopPropagation();
        const { value } = item;

        let values = [];

        if (type === 'nested') {
          if (item.hasNestedDropdown) {
            values = [...selected.slice(0, level), item, {}];
          } else {
            values = [...selected.slice(0, level), item];
          }
        } else if (type === 'multi') {
          const includes = selectedValues.includes(value);

          if (includes) {
            values = [...selected.filter(i => i.value !== value)];
          } else {
            values = [...selected, item];
          }
        } else {
          values = optionsTemplate ? [item.label] : [item];
        }

        setSelected(values);

        if (typeof onSelect === 'function') {
          onSelect(event, optionsTemplate ? item.label : item, values);
        }
      }
    };

    // Key down event in the listbox.
    const handleKeyDown = event => {
      if (enableListboxKeydown) {
        event.preventDefault();
        const { key } = event;

        const count = focusableItemElements.length;

        let values = [];
        let focusId = focusedIndex;
        if (count) {
          if (
            selected.filter(el => el.value !== undefined).length <= level ||
            key === keyCode.HOME
          ) {
            focusId = 0;
            if (focusableItemElements[focusId]) {
              const currentlyFocusedItem = focusableItemObject[focusId];
              if (multiselect) {
                setFocusedValue(currentlyFocusedItem.value);
                values = selected;
              } else {
                values = [...selected.slice(0, level), currentlyFocusedItem];
              }
            }

            setFocusedIndex(focusId);
          } else if (key === keyCode.END) {
            focusId = count - 1;
            const currentlyFocusedItem = focusableItemObject[focusId];
            if (multiselect) {
              setFocusedValue(currentlyFocusedItem.value);
              values = selected;
            } else {
              values = [...selected.slice(0, level), currentlyFocusedItem];
            }

            setFocusedIndex(focusId);
          } else if (
            key === keyCode.ESC ||
            key === keyCode.ESC_IE ||
            key === keyCode.TAB
          ) {
            if (event.key === keyCode.ESC || event.key === keyCode.ESC_IE) {
              event.nativeEvent.stopImmediatePropagation();
            }
            values = [...selected.slice(0, level)];
          } else if (key === keyCode.UP) {
            focusId = focusedIndex - 1 < 0 ? 0 : focusedIndex - 1;

            if (focusableItemElements[focusId]) {
              const currentlyFocusedItem = focusableItemObject[focusId];
              if (multiselect) {
                setFocusedValue(currentlyFocusedItem.value);
                values = selected;
              } else {
                values = [...selected.slice(0, level), currentlyFocusedItem];
              }
            }
            setFocusedIndex(focusId);
          } else if (key === keyCode.DOWN) {
            focusId =
              focusedIndex + 1 < count - 1 ? focusedIndex + 1 : count - 1;

            if (focusableItemElements[focusId]) {
              const currentlyFocusedItem = focusableItemObject[focusId];
              if (multiselect) {
                setFocusedValue(currentlyFocusedItem.value);
                values = selected;
              } else {
                values = [...selected.slice(0, level), currentlyFocusedItem];
              }
            }
            setFocusedIndex(focusId);
          } else if (key === keyCode.RIGHT || key === keyCode.ENTER) {
            const currentlyFocusedItem = focusableItemObject[focusId];
            if (currentlyFocusedItem) {
              const includes = selectedValues.includes(
                currentlyFocusedItem.value
              );
              if (multiselect) {
                if (includes) {
                  values = [
                    ...selected.filter(
                      i => i.value !== currentlyFocusedItem.value
                    )
                  ];
                } else {
                  values = [...selected, currentlyFocusedItem];
                }
              } else if (
                currentlyFocusedItem.children &&
                currentlyFocusedItem.children.length
              ) {
                // To select first element when opening a nested ListBox
                values = [
                  ...selected.slice(0, level),
                  currentlyFocusedItem,
                  currentlyFocusedItem.children[0]
                ];
              } else if (key === keyCode.ENTER) {
                // If enter on childless option, select it.
                values = [...selected.slice(0, level), currentlyFocusedItem];
              } else {
                // If right, do nothing.
                values = selected;
              }
            }
          } else if (key === keyCode.LEFT) {
            if (level > 0) {
              values = [...selected.slice(0, level)];
            } else {
              values = selected;
            }
          } else {
            // If unlisted key pressed, do nothing.
            return;
          }
        }

        // Type ahead function. Currently only for the first character.
        if (isValidChar(event.key)) {
          const char = event.key.toLowerCase();
          const start = focusedIndex + 1 === count ? 0 : focusedIndex + 1;

          focusId = getCharIndex({
            char,
            count,
            focusableItemElements,
            start
          });

          if (focusId === -1) {
            focusId = getCharIndex({
              char,
              count,
              focusableItemElements,
              start: 0
            });
          }

          if (focusId > -1) {
            const currentlyFocusedItem = focusableItemObject[focusId];
            if (multiselect) {
              setFocusedValue(currentlyFocusedItem.value);
            } else {
              values = [currentlyFocusedItem];
            }
          }
          setFocusedIndex(focusId);
        }
        // Scroll the listbox to the currently focused list item.
        scrollListboxTo(
          {
            element: focusableItemElements[focusId],
            listboxNode: listboxRef.current
          },
          onLoadMoreItems,
          numberValuesRendered
        );

        const item = focusableItemObject[focusId];

        // setSelected(values);
        if (typeof onKeyDown === 'function') {
          onKeyDown(event, item, values, focusId);
        }
      }
    };

    function getSelectableItems() {
      return items.flatMap(item => {
        if (!item.categories || item.categories === []) return [item];
        return item.categories.map(cat => ({ ...cat, parent: item.label }));
      });
    }

    function getFocusedItem() {
      const selectableItems = getSelectableItems();
      return selectableItems[focusedIndex]?.label ?? selectedListItems[0].label;
    }

    return (
      <div
        className={classnames(CSS_PREFIX, className)}
        ref={ref}
        {...remainingProps}
      >
        {cloneElement(children, {
          'aria-activedescendant':
            // querySelector as workaround becauase category mapping breaks focusedIndex
            selectedListItems && selectedListItems.length > 0
              ? listboxRef?.current?.querySelector(
                  `[id^="${kebabCase(getFocusedItem())}-list-item-${level}-"]`
                )?.id
              : undefined,
          'aria-multiselectable': multiselect || undefined,
          children:
            children.props &&
            // eslint-disable-next-line react/prop-types
            recursiveMap(children.props.children, (child, idx) => {
              let nestedProps = null;
              if (type === 'nested') {
                nestedProps = {
                  index: idx,
                  level,
                  hasNestedDropdown: Boolean(child.props.itemChildren),
                  children: child.props.itemChildren
                };
              }
              const getId = isListBoxKeyDown => {
                if (isListBoxKeyDown) {
                  return optionsTemplate && child.props.templateLabel?.label
                    ? kebabCase(child.props.templateLabel.label)
                    : kebabCase(child.props.label);
                }
                return (
                  kebabCase(child.props.templateLabel?.label) ??
                  kebabCase(child.props.label)
                );
              };
              return cloneElement(child, {
                'aria-selected': enableListboxKeydown
                  ? undefined
                  : (child.props.value &&
                      selectedValues &&
                      selectedValues.includes(child.props.value)) ||
                    undefined,
                'aria-hidden': child.props.disabled || undefined,
                disabled: child.props.disabled,
                focus: multiselect && child.props.value === focusedValue,
                id: `${getId(enableListboxKeydown)}-list-item-${level}-${idx}`,
                listItemLabel: child.props.label,
                onClick: e =>
                  handleClick(e, {
                    value: child.props.value,
                    label: child.props.templateLabel,
                    isBack: child.props.isBack,
                    ...nestedProps
                  }),
                role: enableListboxKeydown ? 'menuitem' : 'option'
              });
            }),
          onKeyDown: handleKeyDown,
          onScroll: onScrollDown,
          ref: listboxRef,
          tabIndex
        })}
      </div>
    );
  }
);

CybsListboxContainer.propTypes = {
  /**
   * @ignore
   */
  children: PropTypes.oneOfType([
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string
      })
    )
  ]),

  /**
   * @ignore
   */
  className: PropTypes.string,

  /**
   *If `true`, key down callback is fired on the listbox itself for navigating in the list.
   */
  enableListboxKeydown: PropTypes.bool,

  /**
   * Index of the currently focused item.
   */
  focusedIndex: PropTypes.number,

  /**
   * Value of the currently focused item.
   */
  focusedValue: PropTypes.string,

  /**
   * List of items in the listbox.
   */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      categories: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          value: PropTypes.string
        })
      ),
      disabled: PropTypes.bool,
      label: PropTypes.string,
      value: PropTypes.string
    })
  ).isRequired,
  /**
   * Nested depth of the listbox.
   */
  level: PropTypes.number,

  /**
   * If `true`, many list items can be selected.
   */
  multiselect: PropTypes.bool,

  /**
   * Name
   */
  name: PropTypes.string,

  /**
   * optionsTemplate to render multiple attributes in the listbox
   */
  optionsTemplate: PropTypes.shape({
    optionValues: PropTypes.arrayOf(
      PropTypes.shape({ value: PropTypes.string.isRequired })
    ),
    renderOption: PropTypes.func.isRequired
  }),

  /**
   * Callback fired on key down in the listbox.
   */
  onKeyDown: PropTypes.func,
  /**
  /**
   * Value for the number of items currently rendered
   */
  numberValuesRendered: PropTypes.number,

  /**
   * Callback fired when a list item is selected.
   */
  onSelect: PropTypes.func,

  /**
   * Callback fired when scroll reaches bottom of scrollable list
   */
  onScrollBottom: PropTypes.func,

  /**
   * Should the listbox scroll to the currently selected item on load?
   */
  scrollOnLoad: PropTypes.bool,

  /**
   * Selected List items values
   */
  selectedValues: PropTypes.arrayOf(PropTypes.string),

  /**
   * Selected List items
   */
  selectedListItems: PropTypes.oneOfType([
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string
      })
    )
  ]),

  /**
   * Tab Index
   */
  tabIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * Function to Load More Items aka rendor more list items
   */

  onLoadMoreItems: PropTypes.func.isRequired,

  /**
   * Height Of Item
   */

  heightOfItem: PropTypes.number.isRequired,

  /**
   * Different type of dropdowns.
   */
  type: PropTypes.oneOf(['multi', 'single', 'nested'])
};

CybsListboxContainer.defaultProps = {
  children: undefined,
  className: undefined,
  enableListboxKeydown: true,
  focusedIndex: -1,
  focusedValue: '',
  level: 0,
  multiselect: false,
  name: '',
  optionsTemplate: undefined,
  onKeyDown: () => {},
  onSelect: () => {},
  onScrollBottom: () => {},
  scrollOnLoad: false,
  selectedValues: [''],
  numberValuesRendered: null,
  selectedListItems: [{ value: '', label: '' }],
  tabIndex: '-1',
  type: 'single'
};

CybsListboxContainer.displayName = 'CybsListboxContainer';

export default CybsListboxContainer;
